import React, { useEffect, useState } from 'react';
import { Modal, Form, Input, Select, DatePicker, Row, Col, message, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { Visitor, VisitorType, VisitorStatus, IDType } from '@/types/residents/visitor';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import { formatFirestoreTimestamp } from '@/utils/date';


const { TextArea } = Input;

type HostType = 'resident' | 'employee';

interface VisitorModalProps {
  open: boolean;
  visitor?: Visitor | null;
  onSuccess: (data: Omit<Visitor, 'id'>) => void;
  onCancel: () => void;
}

interface FormValues {
  type: VisitorType;
  status: VisitorStatus;
  name: string;
  phone?: string;
  email?: string;
  idType?: IDType;
  idNumber?: string;
  hostType: HostType;
  residentId?: string;
  employeeId?: string;
  expectedArrival: Dayjs;
  expectedDeparture: Dayjs;
  actualArrival?: Dayjs;
  actualDeparture?: Dayjs;
  purpose: string;
  numberOfVisitors: number;
  vehicleType?: string;
  vehicleNumber?: string;
  vehicleColor?: string;
  notes?: string;
}

const VisitorModal: React.FC<VisitorModalProps> = ({
  open,
  visitor,
  onSuccess,
  onCancel,
}) => {
  const { t } = useTranslation(['visitors', 'common']);
  const [form] = Form.useForm<FormValues>();
  const [hostType, setHostType] = useState<HostType>('resident');
  const [selectedResident, setSelectedResident] = useState<{ id: string; name: string } | null>(null);
  const [selectedEmployee, setSelectedEmployee] = useState<{ id: string; name: string } | null>(null);

  useEffect(() => {
    if (open && visitor) {
      // Determine host type based on existing data
      let hostTypeValue: HostType = 'resident';
      if (visitor.employee) {
        hostTypeValue = 'employee';
      }

      form.setFieldsValue({
        ...visitor,
        expectedArrival: visitor.expectedArrival ? dayjs(formatFirestoreTimestamp(visitor.expectedArrival)) : undefined,
        expectedDeparture: visitor.expectedDeparture ? dayjs(formatFirestoreTimestamp(visitor.expectedDeparture)) : undefined,
        actualArrival: visitor.actualArrival ? dayjs(formatFirestoreTimestamp(visitor.actualArrival)) : undefined,
        actualDeparture: visitor.actualDeparture ? dayjs(formatFirestoreTimestamp(visitor.actualDeparture)) : undefined,
        hostType: hostTypeValue,
        residentId: visitor.resident?.id,
        employeeId: visitor.employee?.id,
        vehicleType: visitor.vehicleDetails?.type,
        vehicleNumber: visitor.vehicleDetails?.number,
        vehicleColor: visitor.vehicleDetails?.color,
      });
      setHostType(hostTypeValue);
      
      // Set the selected data
      if (visitor.resident) {
        setSelectedResident(visitor.resident);
      }
      if (visitor.employee) {
        setSelectedEmployee(visitor.employee);
      }
    } else if (open) {
      form.resetFields();
      form.setFieldsValue({
        expectedArrival: dayjs(),
        expectedDeparture: dayjs().add(1, 'day'),
        status: 'pending',
        type: 'guest',
        numberOfVisitors: 1,
        hostType: 'resident',
      });
      setHostType('resident');
      setSelectedResident(null);
      setSelectedEmployee(null);
    }
  }, [open, visitor, form]);

  const handleHostTypeChange = (value: HostType) => {
    setHostType(value);
    // Clear the other fields when switching
    form.setFieldsValue({
      residentId: undefined,
      employeeId: undefined,
    });
    setSelectedResident(null);
    setSelectedEmployee(null);
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      
      const data: Omit<Visitor, 'id'> = {
        type: values.type,
        status: values.status,
        name: values.name,
        phone: values.phone,
        email: values.email,
        idType: values.idType,
        idNumber: values.idNumber,
        expectedArrival: values.expectedArrival.toDate(),
        expectedDeparture: values.expectedDeparture.toDate(),
        actualArrival: values.actualArrival?.toDate(),
        actualDeparture: values.actualDeparture?.toDate(),
        purpose: values.purpose,
        numberOfVisitors: values.numberOfVisitors,
        vehicleDetails: values.vehicleType ? {
          type: values.vehicleType,
          number: values.vehicleNumber || '',
          color: values.vehicleColor || '',
        } : undefined,
        notes: values.notes,
      };

      // Only add fields that are not undefined
      if (values.hostType === 'resident' && values.residentId && selectedResident) {
        data.resident = selectedResident;
      }
      
      if (values.hostType === 'employee' && values.employeeId && selectedEmployee) {
        data.employee = selectedEmployee;
      }

      onSuccess(data);
    } catch (error) {
      message.error(t('common:error'));
    }
  };

  const renderHostSelector = () => {
    switch (hostType) {
      case 'resident':
        return (
          <Form.Item
            name="residentId"
            label={t('visitors:resident')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <ResidentSelector onResidentDataChange={setSelectedResident} />
          </Form.Item>
        );
      case 'employee':
        return (
          <Form.Item
            name="employeeId"
            label={t('visitors:employee')}
            rules={[{ required: true, message: t('common:fieldRequired') }]}
          >
            <EmployeeSelector onEmployeeDataChange={setSelectedEmployee} />
          </Form.Item>
        );
      default:
        return null;
    }
  };

  return (
    <Modal
      title={visitor ? t('visitors:editVisitor') : t('visitors:addVisitor')}
      open={open}
      onOk={handleSubmit}
      onCancel={onCancel}
      okText={visitor ? t('common:actions.update') : t('common:actions.create')}
      cancelText={t('common:actions.cancel')}
      destroyOnHidden
      width={800}
    >
      <Form form={form} layout="vertical" style={{ paddingTop: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="type"
              label={t('visitors:type')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                            <Select.Option value="guest">{t('visitors:types.guest')}</Select.Option>
            <Select.Option value="service">{t('visitors:types.service')}</Select.Option>
            <Select.Option value="delivery">{t('visitors:types.delivery')}</Select.Option>
            <Select.Option value="contractor">{t('visitors:types.contractor')}</Select.Option>
            <Select.Option value="other">{t('visitors:types.other')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('visitors:status')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select>
                            <Select.Option value="pending">{t('visitors:statuses.pending')}</Select.Option>
            <Select.Option value="approved">{t('visitors:statuses.approved')}</Select.Option>
            <Select.Option value="rejected">{t('visitors:statuses.rejected')}</Select.Option>
            <Select.Option value="checked_in">{t('visitors:statuses.checked_in')}</Select.Option>
            <Select.Option value="checked_out">{t('visitors:statuses.checked_out')}</Select.Option>
            <Select.Option value="expired">{t('visitors:statuses.expired')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="name"
              label={t('visitors:name')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Input placeholder={t('visitors:namePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="phone"
              label={t('visitors:phone')}
            >
              <Input placeholder={t('visitors:phonePlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="email"
              label={t('visitors:email')}
            >
              <Input placeholder={t('visitors:emailPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="numberOfVisitors"
              label={t('visitors:numberOfVisitors')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <InputNumber min={1} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="idType"
              label={t('visitors:idType')}
            >
              <Select>
                            <Select.Option value="passport">{t('visitors:idTypes.passport')}</Select.Option>
            <Select.Option value="drivers_license">{t('visitors:idTypes.drivers_license')}</Select.Option>
            <Select.Option value="national_id">{t('visitors:idTypes.national_id')}</Select.Option>
            <Select.Option value="other">{t('visitors:idTypes.other')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="idNumber"
              label={t('visitors:idNumber')}
            >
              <Input placeholder={t('visitors:idNumberPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="hostType"
              label={t('visitors:hostType')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select onChange={handleHostTypeChange}>
                            <Select.Option value="resident">{t('visitors:hostTypes.resident')}</Select.Option>
            <Select.Option value="employee">{t('visitors:hostTypes.employee')}</Select.Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            {renderHostSelector()}
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="expectedArrival"
              label={t('visitors:expectedArrival')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="expectedDeparture"
              label={t('visitors:expectedDeparture')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="actualArrival"
              label={t('visitors:actualArrival')}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="actualDeparture"
              label={t('visitors:actualDeparture')}
            >
              <DatePicker showTime style={{ width: '100%' }} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="purpose"
          label={t('visitors:purpose')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea rows={3} placeholder={t('visitors:purposePlaceholder')} />
        </Form.Item>

        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="vehicleType"
              label={t('visitors:vehicleType')}
            >
              <Input placeholder={t('visitors:vehicleTypePlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="vehicleNumber"
              label={t('visitors:vehicleNumber')}
            >
              <Input placeholder={t('visitors:vehicleNumberPlaceholder')} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="vehicleColor"
              label={t('visitors:vehicleColor')}
            >
              <Input placeholder={t('visitors:vehicleColorPlaceholder')} />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item
          name="notes"
          label={t('visitors:notes')}
        >
          <TextArea rows={3} placeholder={t('visitors:notesPlaceholder')} />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default VisitorModal; 